/**
 * 계약서 업로드 페이지 (간단한 버전)
 */
import React, { useState } from 'react';
import axios from 'axios';
import ContractForm from '../components/ContractForm';

export default function ContractUpload() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [files, setFiles] = useState([]);        // [CT_MULTI_V1] 여러 장 선택 시 파일들 (1장이면 빈 배열)
  const [previews, setPreviews] = useState([]);  // [CT_MULTI_V1] 여러 장 미리보기 (dataURL)
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [extracted, setExtracted] = useState(null);
  // 서버가 저장한 실제 파일명 (multer가 붙인 이름)
  // 브라우저의 file.name(원본 파일명)과 다르므로 반드시 서버 응답값을 써야 한다
  const [savedFileName, setSavedFileName] = useState(null);
  // 백엔드가 DB 형식으로 변환해준 초안 — 저장 전 화면에서 편집한다
  const [draft, setDraft] = useState(null);
  // 제미나이 원본 JSON 접기/펼치기 (폼에 없는 값 확인용)
  const [showRaw, setShowRaw] = useState(false);

  const handleFileSelect = (selectedFile) => {
    // ★ 허용 파일 타입
    const allowedTypes = [
        'image/jpeg', 'image/png', 'image/webp',  // 이미지
        'application/pdf',                         // PDF
        'application/msword',                      // Word (.doc)
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',  // Word (.docx)
        'text/plain'                               // Text
    ];

    if (selectedFile && allowedTypes.includes(selectedFile.type)) {
        setFile(selectedFile);
        
        // 이미지만 미리보기 생성
        if (selectedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
            setPreview(e.target.result);
        };
        reader.readAsDataURL(selectedFile);
        } else {
        setPreview(null);  // 이미지가 아니면 미리보기 없음
        }
        
        setMessage(null);
    } else {
        setMessage({ type: 'error', text: '이미지, PDF, Word, 텍스트 파일만 선택 가능합니다' });
    }
    };

  // ──────────────────────────────────────────────────────────────
  // [CT_MULTI_V1] 파일 선택 (여러 개 가능)
  //  - 1개  : 기존 handleFileSelect 그대로 (이미지·PDF·Word·텍스트)
  //  - 2~5개: "한 계약서의 여러 쪽"으로 보고 이미지만 허용 → handleUpload 가 /upload-multi 로 보냄
  //  - 쪽 순서 = 파일 이름순 (스캐너 파일명 Scan_날짜_시각 이면 스캔한 순서와 같음)
  // ──────────────────────────────────────────────────────────────
  const handleFilesSelect = (fileList) => {
    const arr = Array.from(fileList || [])
      .sort((a, b) => a.name.localeCompare(b.name, 'ko', { numeric: true }));
    setFiles([]);
    setPreviews([]);
    if (arr.length <= 1) { handleFileSelect(arr[0]); return; }

    const okType = ['image/jpeg', 'image/png', 'image/webp'];
    const bad = arr.find((f) => !okType.includes(f.type));
    if (bad) {
      setFile(null); setPreview(null);
      setMessage({ type: 'error', text: `여러 장 등록은 이미지(JPG·PNG)만 가능합니다. PDF·Word 는 1개씩 등록하세요. (${bad.name})` });
      return;
    }
    if (arr.length > 5) {
      setFile(null); setPreview(null);
      setMessage({ type: 'error', text: '한 계약서는 최대 5장까지 선택할 수 있습니다' });
      return;
    }
    setFiles(arr);
    setFile(arr[0]);        // handleUpload 의 "파일 선택 여부" 검사 통과용
    setPreview(null);       // 1장용 미리보기는 끄고 아래 여러 장 미리보기 사용
    setMessage({ type: 'success', text: `${arr.length}장을 한 계약서로 분석합니다 (이름순 = 쪽 순서)` });
    Promise.all(arr.map((f) => new Promise((ok) => {
      const r = new FileReader();
      r.onload = (e) => ok(e.target.result);
      r.readAsDataURL(f);
    }))).then(setPreviews);
  };

  const handleUpload = async () => {
    if (!file) {
      setMessage({ type: 'error', text: '파일을 선택해주세요' });
      return;
    }

    setLoading(true);
    const formData = new FormData();
    if (files.length > 1) files.forEach((f) => formData.append('files', f));   // [CT_MULTI_V1] 여러 장
    else formData.append('file', file);

    try {
      const response = await axios.post(files.length > 1 ? '/api/contracts/upload-multi' : '/api/contracts/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setExtracted(response.data.extractedData);
        setSavedFileName(response.data.tempFilePath);  // 서버 저장명 보관
        setDraft(response.data.contractDraft);          // 편집용 초안
        setMessage({ type: 'success', text: '분석 완료!' });
      } else {
        setMessage({ type: 'error', text: '분석 실패: ' + response.data.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '오류: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h1>📄 계약서 등록</h1>

      <div style={{ border: '2px dashed #2196F3', padding: '30px', textAlign: 'center', borderRadius: '8px', marginBottom: '20px' }}>
        <h3>계약서 이미지를 선택하세요</h3>
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>2장 이상 계약서(특약·공유자가 다음 장으로 넘어간 것)는 이미지를 함께 선택하면 한 계약서로 분석합니다</div>
        <input
            type="file"
            accept="image/*,.pdf,.doc,.docx,.txt"
            multiple
            onChange={(e) => handleFilesSelect(e.target.files)}
            title="이미지(JPG, PNG), PDF, Word 문서 지원"
            />
      </div>

      {previews.length > 1 && (
        <div style={{ marginBottom: '20px' }}>
          <h4>📸 미리보기 ({previews.length}장 → 계약서 1건으로 분석)</h4>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {previews.map((src, i) => (
              <div key={i} style={{ textAlign: 'center', fontSize: '12px', color: '#555' }}>
                <img src={src} alt={`page${i + 1}`} style={{ maxHeight: '220px', maxWidth: '100%', borderRadius: '6px', border: '1px solid #ddd' }} />
                <div>{i + 1}쪽 · {files[i] && files[i].name}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {preview && (
        <div style={{ marginBottom: '20px' }}>
          <h4>📸 미리보기</h4>
          <img src={preview} alt="preview" style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px' }} />
        </div>
      )}

      <button
        onClick={handleUpload}
        disabled={!file || loading}
        style={{
          backgroundColor: '#2196F3',
          color: 'white',
          border: 'none',
          padding: '10px 20px',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px',
          marginBottom: '20px'
        }}
      >
        {loading ? '분석 중...' : '🤖 AI로 분석하기'}
      </button>

        {extracted && (
        <div style={{ backgroundColor: '#f5f5f5', padding: '20px', borderRadius: '8px', marginTop: '20px' }}>
      
            <h3>📋 추출된 정보</h3>
            
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
              추출된 내용을 확인하고 필요하면 수정한 뒤 저장하세요.
            </div>

            {/* 공용 입력 폼 — 등록 전 검토·수정 (담당측 지정 포함) */}
            {draft && (
              <ContractForm
                value={draft}
                onChange={setDraft}
                showOurSide={true}
              />
            )}

            {/* 제미나이 원본 JSON (폼에 없는 값 확인용, 기본 접힘) */}
            <div style={{ marginBottom: '15px' }}>
              <button
                onClick={() => setShowRaw(!showRaw)}
                style={{
                  width: '100%', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0',
                  padding: '7px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px',
                }}
              >
                {showRaw ? '▲ 원본 JSON 접기' : '▼ 원본 JSON 보기'}
              </button>
              {showRaw && (
                <pre style={{
                  backgroundColor: '#fff', padding: '15px', borderRadius: '4px',
                  overflow: 'auto', maxHeight: '300px', fontSize: '11px', marginTop: '6px',
                }}>
                  {JSON.stringify(extracted, null, 2)}
                </pre>
              )}
            </div>
            
            {/* 저장 버튼 */}
            <button
            onClick={async () => {
                setLoading(true);
                try {
                // 편집한 draft(DB 형식)를 /save 가 기대하는 제미나이 형식으로 되돌린다
                // (저장 로직은 기존 형식을 그대로 쓰므로 여기서만 변환한다)
                const response = await axios.post('/api/contracts/save', {
                    tempFilePath: savedFileName,  // ★ file.name(원본명) 아님
                    contractType: draft.contract_type,
                    location: {
                        sido: draft.location_sido,
                        gungu: draft.location_gungu,
                        dong: draft.location_dong,
                        jibun: draft.location_jibun,
                        fullAddress: draft.location_full_address,
                    },
                    parties: {
                        landlords: draft.landlord_info || [],
                        tenants: draft.tenant_info || [],
                    },
                    // ★ brokers는 parties 밖 최상위
                    brokers: draft.broker_info || [],
                    ourSide: draft.our_side,   // 공동중개 담당측 (화면에서 지정)
                    amounts: draft.amount_info,
                    specialClauses: draft.special_clauses,
                    contractDate: draft.contract_date,
                    contractStartDate: draft.contract_start_date,
                    contractEndDate: draft.contract_end_date
                });

                if (response.data.success) {
                  setMessage({ type: 'success', text: `저장 완료! (계약ID: ${response.data.contractId})` });
                  
                  // ★ 2초 후 자동으로 리스트로 이동
                  setTimeout(() => {
                    window.location.href = '/contracts/list';
                  }, 2000);
                } else {
                    setMessage({ type: 'error', text: '저장 실패: ' + response.data.message });
                }
                } catch (error) {
                setMessage({ type: 'error', text: '저장 오류: ' + error.message });
                } finally {
                setLoading(false);
                }
            }}
            disabled={loading}
            style={{
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                marginTop: '15px'
            }}
            >
            {loading ? '저장 중...' : '💾 정보 저장'}
            </button>

          <p style={{ color: '#666', fontSize: '12px', marginTop: '10px' }}>
            ⚠️ 위 정보를 확인하고 필요시 수정한 후 저장해주세요.
          </p>
        </div>
      )}

{/* ★ 메시지를 extracted 블록 밖에 추가 */}
      {message && (
        <div style={{
          padding: '16px',
          marginBottom: '16px',
          marginTop: '20px',
          borderRadius: '4px',
          backgroundColor: message.type === 'error' ? '#ffebee' : '#e8f5e9',
          color: message.type === 'error' ? '#c62828' : '#2e7d32',
          border: `1px solid ${message.type === 'error' ? '#ef5350' : '#66bb6a'}`
        }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>
            {message.text}
          </div>
          
          {/* ★ 저장 완료 시에만 버튼 표시 */}
          {message.type === 'success' && (
            <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
              <button
                onClick={() => {
                  // 리셋
                  setFile(null);
                  setPreview(null);
                  setExtracted(null);
                  setSavedFileName(null);
                  setDraft(null);
                  setMessage(null);
                }}
                style={{
                  backgroundColor: '#2196F3',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 'bold'
                }}
              >
                🔄 다시 등록하기
              </button>
              
              <button
                onClick={() => {
                  // 캘린더 페이지로 이동
                  window.location.href = '/contracts/calendar';
                  // 또는 if (navigate): navigate('/?tab=calendar');
                }}
                style={{
                  backgroundColor: '#FF9800',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 'bold'
                }}
              >
                📅 캘린더에서 확인
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}